export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPercent(value: number, decimals = 1): string {
  const sign = value > 0 ? '+' : value < 0 ? '' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * Formats risk display. The decision.riskPct column stores riskAmount (dollars)
 * when > 1, or actual risk percentage (0–1) when <= 1.
 */
export function formatRiskDisplay(riskPct: number): string {
  if (!Number.isFinite(riskPct)) return '—';
  if (riskPct > 1) {
    return formatCurrency(riskPct);
  }
  return `${(riskPct * 100).toFixed(2)}%`;
}

export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatDate(date: string | Date, includeTime = false): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (includeTime) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
}

export function formatDuration(duration: string): string {
  return duration;
}

/**
 * Elapsed time from `isoStart` to `endMs`, using the same m / h / d buckets as
 * closed trades in `api/endpoints` (`toTrade`). For the first full minute,
 * uses second precision so open-position duration can update in real time.
 */
export function formatElapsedSinceOpen(isoStart: string, endMs: number = Date.now()): string {
  const start = Date.parse(isoStart);
  if (!Number.isFinite(start)) return '—';
  const elapsedMs = endMs - start;
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0) return '—';
  const mins = Math.round(elapsedMs / 60_000);
  if (mins < 1) {
    return `${Math.floor(elapsedMs / 1000)}s`;
  }
  if (mins < 60) {
    return `${mins}m`;
  }
  if (mins < 1440) {
    return `${Math.round(mins / 60)}h`;
  }
  return `${Math.round(mins / 1440)}d`;
}

export function formatRMultiple(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}R`;
}

export function formatPrice(value: number): string {
  if (value >= 1000) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (value >= 1) {
    return value.toFixed(4);
  }
  return value.toFixed(8);
}

/** Maps EXIT_SIGNAL skip reasons to human-readable labels. */
export const EXIT_SIGNAL_SKIP_REASON_LABELS: Record<string, string> = {
  exit_signal_skipped: 'Skip count not reached (need more exit signals)',
  position_in_loss: 'Position in loss (requirePositiveProfit blocks exit)',
  profit_gate_not_met: 'R-multiple below profit gate (e.g. 0.3R)',
  hold_time_not_met: 'Minimum hold time not met',
  stagger_levels_exhausted: 'All stagger levels already used',
  last_level_reserved: 'Last level reserved (closeLastOnExit off)',
  partial_qty_too_small: 'Partial close below minimum lot size',
  stagger_rejected: 'Stagger gates not met',
  exit_signal_ignored: 'EXIT_SIGNAL ignored (Ignore EXIT_SIGNAL setting on)',
  no_position_to_exit: 'No position to exit',
  mt5_command_failed: 'MT5 command failed',
  breakout_partial_disabled: 'Breakout partial exit disabled',
  no_position_for_breakout: 'No position for breakout',
  breakout_direction_mismatch: 'Breakout direction does not match position',
  breakout_signal_skipped: 'Breakout signal skipped (skip first N)',
  breakout_partials_exhausted: 'Max breakout partials already taken',
  breakout_rejected: 'Breakout gates not met',
  long_breakout_disabled: 'LONG_BREAKOUT disabled',
  short_breakout_disabled: 'SHORT_BREAKOUT disabled',
};

/** Maps raw exit reason codes to human-readable labels. */
const EXIT_REASON_LABELS: Record<string, string> = {
  MT5_CLOSE: 'Closed by MT5 (opposite signal)',
  MT5_PARTIAL_TP_FINAL: 'Closed by MT5 (final partial TP)',
  EXIT_SIGNAL: 'Closed on TradingView exit signal',
  EXIT_SIGNAL_STAGGER: 'Closed on exit signal (stagger final)',
  REVERSAL: 'Closed on opposite signal (reversal)',
  KILL_SWITCH: 'Closed by kill switch',
  MANUAL_FLATTEN: 'Closed by operator (manual flatten)',
  MCP_MANUAL_CLOSE: 'Closed via MCP',
  reconciliation_missing_on_exchange: 'Closed (reconciliation: missing on exchange)',
  take_profit: 'Take profit hit',
  stop_loss: 'Stop loss hit',
  manual: 'Manual close',
  trailing_stop: 'Trailing stop hit',
  MAE_CUT: 'Closed via MAE cut (drawdown exceeded threshold)',
  WEAK_PARTICIPATION_EXIT: 'Closed via weak participation (low MFE vs adverse excursion)',
  'MT5_CLOSE:weak_participation': 'Closed via weak participation (MT5)',
  ZERO_MFE_EXIT: 'Closed via zero-MFE early exit (trade never participated)',
  'MT5_CLOSE:zero_mfe': 'Closed via zero-MFE early exit (MT5)',
  REGIME_BLOCK: 'Closed when regime blocked a new entry (same-side position)',
  SIGNAL_FLIP: 'Closed on opposite entry signal (flip; reversal off)',
  'MT5_CLOSE:signal_flip': 'Closed on opposite entry signal (flip; MT5)',
  MFE_PARTIAL: 'MFE partial close',
  BREAKOUT_PARTIAL: 'Breakout partial close (TBO)',
  TRAILING_STOP: 'Trailing stop',
};

/**
 * Formats exit reason for display. Returns human-readable label; falls back to
 * title-cased raw value for unknown codes.
 *
 * v0.0.39+: signal_flip exit reasons can carry an optional `:tier=A|B|C`
 * suffix indicating the post-cap tier of the opposite signal that triggered
 * the flip-close (encoded by `tryHandleOppositeSignalFullExit`). The suffix
 * is parsed off and appended to the rendered label so operators see at a
 * glance which quality of opposite signal got through the flip-tier gate.
 *
 * Examples:
 *   "MT5_CLOSE:signal_flip:tier=C"  → "Closed on opposite entry signal (flip; MT5) — opposite signal tier C"
 *   "MT5_CLOSE:signal_flip"          → "Closed on opposite entry signal (flip; MT5)"
 *   "SIGNAL_FLIP:tier=B"             → "Closed on opposite entry signal (flip; reversal off) — opposite signal tier B"
 */
export function formatExitReason(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '—';

  /**
   * Strip optional tier suffix and resolve the base reason. Anchored to
   * end-of-string so a `:tier=` substring buried mid-reason (none today,
   * but defensive) does not get split.
   */
  const tierMatch = raw.match(/^(.+):tier=([A-D])$/);
  const baseReason = tierMatch?.[1] ?? raw;
  const tier = tierMatch?.[2] ?? null;

  const label = EXIT_REASON_LABELS[baseReason];
  const baseLabel = label
    ?? baseReason
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

  return tier ? `${baseLabel} — opposite signal tier ${tier}` : baseLabel;
}

/** Formats EXIT_SIGNAL skip reason for display. */
export function formatExitSignalSkipReason(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const label = EXIT_SIGNAL_SKIP_REASON_LABELS[raw];
  if (label) return label;
  return raw
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}
